import React, { useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Phone } from "lucide-react";
import { toast } from "sonner";
import AuthShell from "@/components/AuthShell";
import OtpField from "@/components/auth/OtpField";
import PhoneResetDialog from "@/components/auth/PhoneResetDialog";

export default function Login() {
  const [mode, setMode] = useState("password");            // "password" | "otp"
  const [phone, setPhone] = useState("");
  const [phonePw, setPhonePw] = useState("");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const { loginPhone } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const prefetchedRef = useRef(false);
  const prefetchDashboard = () => {
    if (prefetchedRef.current) return;
    prefetchedRef.current = true;
    import("@/components/DashboardLayout");
    import("@/pages/guest/Dashboard");
    import("@/pages/business/Dashboard");
    import("@/pages/admin/Dashboard");
  };

  const goNext = (u) => {
    toast.success(`Welcome back, ${u.name}`);
    const dest = loc.state?.from?.pathname || (
      u.role === "admin" ? "/admin" :
      u.role === "business" ? "/business" :
      u.role === "sales_partner" ? "/sales" : "/guest");
    nav(dest, { replace: true });
  };

  const requestOtp = async () => {
    if (!phone) return toast.error("Enter your mobile number first");
    setBusy(true); setOtp("");
    try {
      const { data } = await api.post("/auth/phone/request-otp", { phone, purpose: "login" });
      setOtpSent(true);
      if (data.demo_otp) {
        setDemoOtp(data.demo_otp);
        toast.success("On-screen OTP mode — your code is shown below the field");
      } else {
        setDemoOtp("");
        toast.success("OTP sent to your phone");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = mode === "otp" ? { phone, otp_code: otp } : { phone, password: phonePw };
      goNext(await loginPhone(payload));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell
      heading="Welcome back."
      subhead="Log in with your mobile number — OTP or password, your call."
      footerTag="valuepool.app / v1"
    >
      <>
      <Card className="w-full max-w-md p-6 sm:p-8 border border-border bg-card shadow-sm" data-testid="login-card">
        <div className="flex items-center gap-2 mb-1">
          <Phone className="w-4 h-4 text-primary" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Log in</h2>
        </div>
        <p className="text-muted-foreground text-sm mt-1">Mobile number-based login.</p>

        <form className="mt-5 space-y-4" onSubmit={submit}>
          <div>
            <Label>Mobile number</Label>
            <Input data-testid="login-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                   onFocus={prefetchDashboard} required placeholder="Your mobile number" inputMode="tel" />
          </div>

          <div className="flex gap-2 text-xs">
            <button type="button" onClick={() => setMode("password")}
                    className={`px-3 py-1.5 rounded-full transition ${mode === "password" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                    data-testid="login-mode-password">Use password</button>
            <button type="button" onClick={() => setMode("otp")}
                    className={`px-3 py-1.5 rounded-full transition ${mode === "otp" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                    data-testid="login-mode-otp">Use OTP</button>
          </div>

          {mode === "otp" && (
            <OtpField
              value={otp} onChange={setOtp} demoOtp={demoOtp} disabled={!otpSent}
              placeholder={otpSent ? "123456" : "Tap “Send OTP” first"}
              testId="login-otp" demoTestId="login-demo-otp"
              note="— SMS delivery unavailable, on-screen OTP is enabled."
              action={
                <button type="button" onClick={requestOtp} disabled={busy}
                        className="text-xs text-primary hover:underline font-medium" data-testid="login-otp-request">
                  {otpSent ? "Resend OTP" : "Send OTP"}
                </button>
              }
            />
          )}

          {mode === "password" && (
            <div>
              <div className="flex items-center justify-between">
                <Label>Password</Label>
                <button type="button" onClick={() => setResetOpen(true)}
                        className="text-xs text-primary hover:underline font-medium" data-testid="login-forgot">
                  Forgot password?
                </button>
              </div>
              <Input data-testid="login-password" type="password" value={phonePw}
                     onChange={(e) => setPhonePw(e.target.value)}
                     required={mode === "password"} placeholder="••••••••" />
            </div>
          )}

          <Button data-testid="login-submit" disabled={busy} type="submit" className="w-full bg-primary text-primary-foreground">
            {busy ? "Logging in…" : "Log in"}
          </Button>
        </form>

        <div className="mt-6 text-sm text-muted-foreground">
          New here? <Link to="/signup" className="text-primary font-medium hover:underline">Create an account</Link>
        </div>
      </Card>

      {resetOpen && (
        <PhoneResetDialog open={resetOpen} onOpenChange={setResetOpen} initialPhone={phone} />
      )}
      </>
    </AuthShell>
  );
}
